import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import ChartCanvas from "./ChartCanvas";
import DrawingRail from "./DrawingRail";
import ReplayBar from "./ReplayBar";
import ChartContextMenu from "./ChartContextMenu";
import ConfluenceCard from "./ConfluenceCard";
import ObjectToolbar from "./ObjectToolbar";
import { useDrawings } from "../../lib/useDrawings";
import { confluenceStore } from "../../lib/confluenceStore";
import { useChartData } from "../../lib/useChartData";
import { TIMEFRAMES, TICK_TIMEFRAMES, meta, fmtPrice } from "../../lib/symbols";
import { heikinAshi, IndicatorEngine, INDICATOR_PRESETS, StructureEngine, AlertEvaluator, analysisBus, ConfluenceEngine, createDrawing, setEmojiReadyHandler, TwoBOneAState, DEFAULT_STRUCTURE_SETTINGS, isSyntheticTf } from "../../engine";
import { useTheme } from "../../context/ThemeContext";
import { useReplay } from "../../context/ReplayContext";
import { useAlerts } from "../../context/AlertsContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import {
  Camera, Maximize2, Settings2, Undo2, Redo2, ChevronDown, CandlestickChart,
  Activity, Waypoints, History, Search, Plus, Save, Star, PenLine,
} from "lucide-react";
import { useUiStore, useChartBridge } from "../../store/uiStore";
import { DEFAULT_STYLE } from "../../engine/drawings/types";
import { useDerivClock } from "../../hooks/useDerivClock";
import { T50DetailCard } from "./T50Card";
import { useT50Shadow } from "../../hooks/useT50Shadow";
import { t50HitAt } from "../../engine";
import { SymbolCatalog } from "../../engine";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const toLocalInput = (epoch) => {
  const d = new Date(epoch * 1000);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const CHART_TYPES = [
  { id: "candlestick", label: "Candlestick" },
  { id: "hollow", label: "Hollow candles" },
  { id: "ohlc", label: "OHLC bars" },
  { id: "line", label: "Line" },
  { id: "area", label: "Area" },
  { id: "heikin", label: "Heikin Ashi" },
];

function Toggle({ label, checked, onChange, testid, color }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="flex items-center justify-between w-full px-2 py-1.5 rounded-md hover:bg-secondary text-sm"
    >
      <span className="flex items-center gap-2">
        {color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />}
        {label}
      </span>
      <span className={`h-4 w-7 rounded-full relative transition-colors ${checked ? "bg-primary" : "bg-muted"}`}>
        <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${checked ? "left-3.5" : "left-0.5"}`} />
      </span>
    </button>
  );
}

export default function ChartPanel({ symbol, tf, onTfChange, quote }) {
  const { theme } = useTheme();
  const m = meta(symbol);
  const { candles, loading, lastPrice } = useChartData(symbol, tf);

  const ws = useWorkspace();
  const chartSettings = ws?.settings?.chart ?? {};
  const ui = useUiStore();
  const { chartType, setChartType, showVolume, setShowVolume, structureFlags, setStructureFlags, showConfluence, setShowConfluence } = ui;
  const activeInd = useMemo(() => new Set(ui.activeInd), [ui.activeInd]);
  const setBridge = useChartBridge((s) => s.setBridge);
  const [symQuery, setSymQuery] = useState("");
  const [symOpen, setSymOpen] = useState(false);
  const [mobileTools, setMobileTools] = useState(false);
  const [t50Detail, setT50Detail] = useState(null);
  // click on a T50 marker → detail card (structure markers are not drawings, so hit-test them here)
  const onPlotClick = useCallback((e) => {
    const chart = chartRef.current; const f = chart?.currentFrame; const t50 = structureRef.current?.t50;
    if (!f || !t50 || chart.opts.activeTool !== "cross" && chart.opts.activeTool !== "select") return;
    const r = e.currentTarget.getBoundingClientRect();
    const hit = t50HitAt(f, t50, e.clientX - r.left, e.clientY - r.top);
    if (hit) setT50Detail(hit);
  }, []);
  const [clock, setClock] = useState("");
  const derivOffset = useDerivClock();
  useEffect(() => { const id = setInterval(() => setClock(new Date(Date.now() + derivOffset).toUTCString().split(" ")[4]), 1000); return () => clearInterval(id); }, [derivOffset]);
  const symResults = useMemo(() => {
    const q = symQuery.trim().toLowerCase();
    const all = SymbolCatalog.all();
    return (q ? all.filter((x) => x.symbol.toLowerCase().includes(q) || x.display.toLowerCase().includes(q) || x.name.toLowerCase().includes(q)) : all).slice(0, 40);
  }, [symQuery, symOpen]); // eslint-disable-line
  const dr = useDrawings(symbol);
  const [activeTool, setActiveTool] = useState("cross");
  const [toolMeta, setToolMeta] = useState(null);
  const chartRef = useRef(null);
  const selectTool = useCallback((id, meta = null) => { setActiveTool(id); setToolMeta(meta); }, []);
  const [snap, setSnap] = useState(chartSettings.magnet ?? true);
  useEffect(() => { setEmojiReadyHandler(() => chartRef.current?.requestRender()); }, []);
  // Live Deriv barrier preview from the Trade utility (relative barrier ± spot), drawn as a locked transient object.
  const barrierDrawing = useMemo(() => {
    const tb = ui.tradeBarrier;
    if (!tb || tb.symbol !== symbol || !candles.length || tb.price == null) return null;
    const touch = tb.kind === "touch";
    return { id: "__trade-barrier", type: touch ? "touchbarrier" : "hlbarrier", symbol, timeframe: null, points: [{ time: candles[candles.length - 1].time, price: tb.price }],
      style: { ...DEFAULT_STYLE, color: touch ? "#A855F7" : "#3B82F6", dash: [5, 4] }, visible: true, locked: true, meta: { kind: touch ? (tb.ctype === "ONETOUCH" ? "touch" : "notouch") : "hl" } };
  }, [ui.tradeBarrier, symbol, candles]);
  const visibleDrawings = useMemo(() => {
    const list = dr.drawings.filter((d) => !d.timeframe || d.timeframe === tf.id);
    return barrierDrawing ? [...list, barrierDrawing] : list;
  }, [dr.drawings, tf.id, barrierDrawing]);
  const selected = dr.drawings.find((d) => d.id === dr.selectedId) || null;
  const [hover, setHover] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const containerRef = useRef(null);
  const watermarkRef = useRef(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  const { engine: replayEngine, isReplaying, replay } = useReplay();
  const { openManager, alerts } = useAlerts();
  const [replayAt, setReplayAt] = useState(() => toLocalInput(Date.now() / 1000 - 3600));

  // Replay alert preview: run the SAME AlertEvaluator on replayed candles ("would have fired"), never persisted.
  const previewEvalRef = useRef(null);
  useEffect(() => { previewEvalRef.current = isReplaying(symbol) ? new AlertEvaluator() : null; }, [replay.sessionId, symbol]); // eslint-disable-line
  useEffect(() => {
    const ev = previewEvalRef.current;
    if (!ev || !isReplaying(symbol) || !candles.length) return;
    const defs = alerts.filter((a) => a.symbol === symbol && a.timeframe === tf.id).map((a) => ({
      id: a.id, symbol: a.display, type: a.kind, condition: a.operator, params: a.params || {}, cooldownS: a.cooldown_s ?? 0, repeat: a.repeat || "every",
    }));
    if (!defs.length) return;
    for (const fire of ev.evaluate(defs, candles, tf, replay.cursor * 1000)) analysisBus.emit("alertPreview", { symbol, tf, fire, replay: true });
  }, [candles]); // eslint-disable-line

  const displayCandles = useMemo(() => (chartType === "heikin" ? heikinAshi(candles) : candles), [candles, chartType]);

  const toggleInd = (key) => ui.setActiveInd(activeInd.has(key) ? ui.activeInd.filter((k) => k !== key) : [...ui.activeInd, key]);

  const { overlays, panels } = useMemo(() => {
    const active = INDICATOR_PRESETS.filter((p) => activeInd.has(p.key));
    return IndicatorEngine.compute(active, { candles, tf });
  }, [candles, tf, activeInd]);

  const twoBOneARef = useRef(new TwoBOneAState());
  const structure = useMemo(() => StructureEngine.analyze(candles, structureFlags, { ...DEFAULT_STRUCTURE_SETTINGS, twoBOneAState: twoBOneARef.current, twoBOneAMode: isSyntheticTf(tf) ? "tick" : "ohlc" }), [candles, structureFlags, tf]);
  const structureRef = useRef(structure); structureRef.current = structure;
  useT50Shadow(symbol, tf.id, structure?.t50 ?? null, !!structureFlags.t50 && tf.kind === "sec");
  useEffect(() => { if (t50Detail && structure?.t50) { const fresh = structure.t50.events.find((x) => x.id === t50Detail.id); if (fresh && fresh !== t50Detail) setT50Detail(fresh); } }, [structure]); // eslint-disable-line react-hooks/exhaustive-deps
  const confluence = useMemo(() => (showConfluence ? ConfluenceEngine.score(candles, tf) : null), [candles, tf, showConfluence]);
  useEffect(() => { confluenceStore.set(symbol, tf, confluence); }, [confluence, symbol, tf]);

  useEffect(() => { setBridge({ dr, tfId: tf.id, structure, chart: chartRef.current }); }, [dr.drawings, dr.selectedId, tf.id, structure]); // eslint-disable-line
  useEffect(() => () => setBridge(null), []); // eslint-disable-line

  // publish NEW structure events to the analysis bus (seed watermark on first load, no replay)
  useEffect(() => {
    if (!structure) return;
    if (watermarkRef.current == null) {
      const last = structure.events[structure.events.length - 1];
      watermarkRef.current = last ? last.time : 0;
      return;
    }
    watermarkRef.current = StructureEngine.publish(symbol, tf, structure, watermarkRef.current);
  }, [structure, symbol, tf]);
  useEffect(() => { watermarkRef.current = null; }, [symbol, tf]);

  // countdown to next candle
  useEffect(() => {
    const id = setInterval(() => {
      if (tf.kind === "tick") {
        const last = candles[candles.length - 1];
        setCountdown(last ? last.volume : 0);
      } else {
        const now = Math.floor(Date.now() / 1000);
        setCountdown(tf.seconds - (now % tf.seconds));
      }
    }, 250);
    return () => clearInterval(id);
  }, [tf, candles]);

  const confluenceRef = useRef(null);
  useEffect(() => { confluenceRef.current = confluence; }, [confluence]);
  const addDrawing = useCallback((d) => {
    const c = confluenceRef.current;
    if (d.type === "confluencestamp" && c) d = { ...d, meta: { ...d.meta, bull: Math.round(c.bull), bear: Math.round(c.bear) } };
    dr.add(d);
    if (d.type !== "brush" && d.type !== "highlighter") setActiveTool("cross");
  }, [dr.add]); // eslint-disable-line
  const editText = useCallback((id) => {
    const cur = dr.drawings.find((d) => d.id === id);
    const next = window.prompt("Text", cur?.text ?? "");
    if (next != null) dr.update(id, { text: next });
  }, [dr]);

  // Keyboard: Del/Backspace delete · Esc cursor/deselect · Ctrl+D duplicate · Ctrl+Z / Ctrl+Shift+Z undo/redo · Enter finish polyline
  // Shift+H horizontal line · Shift+V vertical line · Alt+T trend · Alt+F fib · Alt+X text
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === "z") { e.preventDefault(); e.shiftKey ? dr.redo() : dr.undo(); return; }
      if (mod && k === "y") { e.preventDefault(); dr.redo(); return; }
      if (e.key === "Escape") { chartRef.current?.cancelDrawing(); selectTool("cross"); dr.setSelectedId(null); return; }
      if (e.key === "Enter") { chartRef.current?.finishPending(); return; }
      if (e.shiftKey && !mod && !e.altKey && k === "h") { e.preventDefault(); selectTool("hline"); return; }
      if (e.shiftKey && !mod && !e.altKey && k === "v") { e.preventDefault(); selectTool("vline"); return; }
      if (e.altKey && !mod) { const m = { t: "trend", f: "fib", x: "text" }[k]; if (m) { e.preventDefault(); selectTool(m); return; } }
      if (!dr.selectedId) return;
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); if (!selected?.locked) dr.remove(dr.selectedId); }
      else if (mod && k === "d") { e.preventDefault(); dr.duplicate(dr.selectedId); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dr, selected, selectTool]);

  const screenshot = () => {
    const canvas = document.querySelector('[data-testid="chart-canvas"]');
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `fela-rio-${m.display}-${tf.id}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  const fullscreen = () => {
    const el = containerRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const readout = hover || (displayCandles.length ? displayCandles[displayCandles.length - 1] : null);
  const price = quote?.price ?? lastPrice;

  const activeTfSet = TICK_TIMEFRAMES.some((t) => t.id === tf.id) ? TICK_TIMEFRAMES : TIMEFRAMES;

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-panel overflow-hidden" data-testid="chart-panel">
      {/* Toolbar (TradingView-style single row) */}
      <div className="flex items-center gap-1 px-2 h-11 border-b border-border shrink-0 overflow-x-auto md:overflow-hidden scrollbar-none whitespace-nowrap [&>*]:shrink-0" data-testid="chart-toolbar">
        <Popover open={symOpen} onOpenChange={setSymOpen}>
          <PopoverTrigger asChild>
            <button data-testid="chart-symbol-btn" className="h-8 px-2 rounded-md flex items-center gap-1.5 text-sm font-bold hover:bg-secondary">
              <Search className="h-3.5 w-3.5 text-muted-foreground" /><span data-testid="chart-symbol">{m.display}</span><ChevronDown className="h-3 w-3 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 p-1.5 bg-popover" data-testid="symbol-search-popover">
            <input autoFocus data-testid="symbol-search-input" value={symQuery} onChange={(e) => setSymQuery(e.target.value)} placeholder="Search Deriv instruments…" className="w-full h-8 px-2 rounded-md border border-border bg-background text-xs outline-none focus:border-primary" />
            <div className="max-h-72 overflow-y-auto scrollbar-thin mt-1">
              {symResults.map((x) => (
                <button key={x.symbol} data-testid={`symbol-result-${x.symbol}`} onMouseDown={() => { ws?.setSymbol(x.symbol); setSymOpen(false); setSymQuery(""); }} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-secondary ${x.symbol === symbol ? "text-primary" : ""}`}>
                  <span className="font-semibold w-20 shrink-0">{x.display}</span><span className="text-muted-foreground truncate">{x.name}</span><span className="ml-auto text-[10px] text-muted-foreground">{x.market_display}</span>
                </button>
              ))}
              {symResults.length === 0 && <div className="p-3 text-xs text-muted-foreground">No instruments match.</div>}
            </div>
          </PopoverContent>
        </Popover>
        {ws && <IconBtn testid="chart-fav-btn" onClick={() => ws.toggleFav(symbol)} icon={ws.favorites.includes(symbol) ? Star : Plus} title={ws.favorites.includes(symbol) ? "Remove from favourites" : "Add to favourites"} active={ws.favorites.includes(symbol)} />}
        <div className="h-5 w-px bg-border mx-1" />
        <div className="flex items-center gap-0.5">
          {TIMEFRAMES.map((t) => (
            <button
              key={t.id}
              data-testid={`tf-${t.id}`}
              onClick={() => onTfChange(t)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                tf.id === t.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="h-5 w-px bg-border mx-1" />
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="tick-tf-btn" className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground flex items-center gap-1">
              Tick <ChevronDown className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-32 p-1 bg-popover">
            {TICK_TIMEFRAMES.map((t) => (
              <button key={t.id} data-testid={`tf-${t.id}`} onClick={() => onTfChange(t)}
                className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-secondary ${tf.id === t.id ? "text-primary" : ""}`}>
                {t.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        <div className="h-5 w-px bg-border mx-1" />

        {/* chart type */}
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="charttype-btn" className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground flex items-center gap-1">
              <CandlestickChart className="h-3.5 w-3.5" /> {CHART_TYPES.find((c) => c.id === chartType)?.label} <ChevronDown className="h-3 w-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-44 p-1 bg-popover">
            {CHART_TYPES.map((c) => (
              <button key={c.id} data-testid={`charttype-${c.id}`} onClick={() => setChartType(c.id)}
                className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-secondary ${chartType === c.id ? "text-primary" : ""}`}>
                {c.label}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* indicators */}
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="indicators-btn" className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground flex items-center gap-1">
              <Activity className="h-3.5 w-3.5" /> Indicators
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-1 bg-popover max-h-[70vh] overflow-y-auto scrollbar-thin">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 py-1">Overlays</div>
            {INDICATOR_PRESETS.filter((p) => ["sma", "ema", "bb", "vwap"].includes(p.id)).map((p) => (
              <Toggle key={p.key} testid={`ind-${p.key}`} label={p.label} color={p.color} checked={activeInd.has(p.key)} onChange={() => toggleInd(p.key)} />
            ))}
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 py-1 mt-1">Panels</div>
            {INDICATOR_PRESETS.filter((p) => ["rsi", "macd", "atr"].includes(p.id)).map((p) => (
              <Toggle key={p.key} testid={`ind-${p.key}`} label={p.label} color={p.color} checked={activeInd.has(p.key)} onChange={() => toggleInd(p.key)} />
            ))}
            <Toggle testid="ind-volume" label="Volume" checked={showVolume} onChange={setShowVolume} />
            <div className="text-[10px] uppercase tracking-wide text-primary px-2 py-1 mt-1">Fela R.I.O Tick Engine</div>
            {INDICATOR_PRESETS.filter((p) => p.id.startsWith("tick")).map((p) => (
              <Toggle key={p.key} testid={`ind-${p.key}`} label={p.label} color={p.color} checked={activeInd.has(p.key)} onChange={() => toggleInd(p.key)} />
            ))}
          </PopoverContent>
        </Popover>

        {/* market structure */}
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="structure-btn" className="px-2.5 py-1 rounded-md text-xs font-medium text-primary hover:bg-secondary flex items-center gap-1">
              <Waypoints className="h-3.5 w-3.5" /> BOS
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-52 p-1 bg-popover">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 py-1">Auto Market Structure</div>
            <Toggle testid="ms-bos" label="Break of Structure" checked={structureFlags.bos} onChange={(v) => setStructureFlags((s) => ({ ...s, bos: v }))} />
            <Toggle testid="ms-choch" label="Change of Character" checked={structureFlags.choch} onChange={(v) => setStructureFlags((s) => ({ ...s, choch: v }))} />
            <Toggle testid="ms-cisd" label="Change In State of Delivery" checked={!!structureFlags.cisd} onChange={(v) => setStructureFlags((s) => ({ ...s, cisd: v }))} />
            <Toggle testid="ms-t50" label="T50 — two-bar decision (50%)" checked={!!structureFlags.t50} onChange={(v) => setStructureFlags((s) => ({ ...s, t50: v }))} />
            <Toggle testid="ms-swings" label="Swing High / Low" checked={structureFlags.swings} onChange={(v) => setStructureFlags((s) => ({ ...s, swings: v }))} />
            <Toggle testid="ms-2b1a" label="2B1A Swing High / Low" checked={!!structureFlags.twoBOneA} onChange={(v) => setStructureFlags((s) => ({ ...s, twoBOneA: v }))} />
            <Toggle testid="ms-fvg" label="Fair Value Gap" checked={structureFlags.fvg} onChange={(v) => setStructureFlags((s) => ({ ...s, fvg: v }))} />
            <Toggle testid="ms-ob" label="Order Blocks" checked={structureFlags.ob} onChange={(v) => setStructureFlags((s) => ({ ...s, ob: v }))} />
            <Toggle testid="ms-liq" label="Liquidity (EQH / EQL)" checked={structureFlags.liq} onChange={(v) => setStructureFlags((s) => ({ ...s, liq: v }))} />
            <div className="text-[10px] uppercase tracking-wide text-primary px-2 py-1 mt-1">Fela R.I.O Score</div>
            <Toggle testid="ms-confluence" label="Confluence card" checked={showConfluence} onChange={setShowConfluence} />
          </PopoverContent>
        </Popover>

        <div className="ml-auto flex items-center gap-0.5">
          <div className="hidden md:flex items-center gap-0.5">
            {ws && <button data-testid="chart-save-btn" onClick={() => { ws.saveSettings("chart", { chartType, defaultSymbol: symbol, defaultInterval: tf.id }); }} title="Save chart layout as default" className="h-8 px-2 rounded-md flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"><Save className="h-3.5 w-3.5" /> Save</button>}
            <IconBtn testid="chart-undo" onClick={dr.undo} icon={Undo2} title="Undo (Ctrl+Z)" />
            <IconBtn testid="chart-redo" onClick={dr.redo} icon={Redo2} title="Redo (Ctrl+Shift+Z)" />
            <IconBtn testid="chart-settings" onClick={() => {}} icon={Settings2} title="Settings" />
            <IconBtn testid="chart-screenshot" onClick={screenshot} icon={Camera} title="Screenshot" />
          </div>
          <IconBtn testid="chart-fullscreen" onClick={fullscreen} icon={Maximize2} title="Fullscreen" />
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="replay-btn" title="Bar replay"
                className={`h-8 px-2.5 rounded-md flex items-center gap-1.5 text-xs font-semibold transition-colors ${isReplaying(symbol) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <History className="h-4 w-4" /> Replay
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-3 bg-popover space-y-2" data-testid="replay-popover">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Tick replay · {m.display}</div>
              <input data-testid="replay-datetime" type="datetime-local" value={replayAt} onChange={(e) => setReplayAt(e.target.value)}
                max={toLocalInput(Date.now() / 1000 - 60)} className="w-full h-9 px-2 rounded-lg border border-border bg-background text-sm font-mono-nums" />
              <div className="grid grid-cols-3 gap-1">
                {[["15m", 900], ["1h", 3600], ["4h", 14400]].map(([l, s]) => (
                  <button key={l} data-testid={`replay-quick-${l}`} onClick={() => setReplayAt(toLocalInput(Date.now() / 1000 - s))}
                    className="h-7 rounded-md border border-border text-xs hover:bg-secondary">{l} ago</button>
                ))}
              </div>
              <button data-testid="replay-start" onClick={() => { const t = Math.floor(new Date(replayAt).getTime() / 1000); if (t) replayEngine.start(symbol, t); }}
                className="w-full h-9 rounded-lg bg-primary text-primary-foreground text-sm font-bold">Start replay</button>
              <p className="text-[10px] text-muted-foreground">Ticks are fed through the same engine as live. Right-click any candle → "Replay from here". Space = play/pause, → tick, shift+→ candle, ← back.</p>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* chart body */}
      <div className="flex flex-1 min-h-0 relative">
        <div className={`${mobileTools ? "flex absolute inset-y-0 left-0 z-20 shadow-xl" : "hidden"} md:flex md:static md:z-auto md:shadow-none`} data-testid="drawing-rail-wrap">
          <DrawingRail active={activeTool} onSelect={(id, meta) => { selectTool(id, meta); setMobileTools(false); }} snap={snap} onToggleSnap={() => setSnap((v) => !v)}
            drawings={dr.drawings} selectedId={dr.selectedId} onSelectObject={dr.setSelectedId} onUpdate={dr.update} onRemove={dr.remove} onClear={dr.clear} tfId={tf.id}
            onLockAll={(v) => dr.replaceAll(dr.drawings.map((d) => ({ ...d, locked: v })))} onHideAll={(v) => { dr.replaceAll(dr.drawings.map((d) => ({ ...d, visible: !v }))); if (v) dr.setSelectedId(null); }} />
        </div>
        <div className="relative flex-1 min-w-0" onClick={onPlotClick} data-testid="chart-plot-area">
          <button data-testid="mobile-tools-btn" onClick={() => setMobileTools((v) => !v)} aria-label="Drawing tools" aria-pressed={mobileTools}
            className={`md:hidden absolute left-2 bottom-3 z-20 h-10 w-10 rounded-full border shadow-lg flex items-center justify-center backdrop-blur ${mobileTools || activeTool !== "cross" ? "bg-primary text-primary-foreground border-primary" : "bg-panel/90 text-muted-foreground border-border"}`}>
            <PenLine className="h-4 w-4" />
          </button>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground z-10" data-testid="chart-loading">
              {isReplaying(symbol) ? `Loading ${m.display} tick history for replay…` : `Streaming ${m.display} from Deriv…`}
            </div>
          )}
          <div className="absolute left-3 top-2 z-10 pointer-events-none select-none max-w-[calc(100%-80px)]" data-testid="chart-header-overlay">
            <div className="flex items-center gap-2 text-[11px] md:text-[12px] font-medium">
              <span className="text-foreground truncate">{m.name}</span><span className="text-muted-foreground shrink-0">· {tf.label} · Deriv</span>
              <span className={`h-2 w-2 rounded-full shrink-0 ${loading ? "bg-muted-foreground" : "bg-buy"} ${loading ? "" : "animate-pulse"}`} />
              {readout ? (
                <span className="font-mono-nums text-[11px] hidden sm:flex items-center gap-2 ml-1" data-testid="chart-ohlc">
                  <span className="text-muted-foreground">O <span className={readout.close >= readout.open ? "text-buy" : "text-sell"}>{fmtPrice(readout.open, m.decimals)}</span></span>
                  <span className="text-muted-foreground">H <span className={readout.close >= readout.open ? "text-buy" : "text-sell"}>{fmtPrice(readout.high, m.decimals)}</span></span>
                  <span className="text-muted-foreground">L <span className={readout.close >= readout.open ? "text-buy" : "text-sell"}>{fmtPrice(readout.low, m.decimals)}</span></span>
                  <span className="text-muted-foreground">C <span className={readout.close >= readout.open ? "text-buy" : "text-sell"}>{fmtPrice(readout.close, m.decimals)}</span></span>
                  <span className={readout.close >= readout.open ? "text-buy" : "text-sell"}>{readout.close >= readout.open ? "+" : ""}{fmtPrice(readout.close - readout.open, m.decimals)} ({readout.open ? (((readout.close - readout.open) / readout.open) * 100).toFixed(2) : "0.00"}%)</span>
                </span>
              ) : <span className="text-[11px] text-muted-foreground" data-testid="chart-loading">{loading ? "Loading market data…" : "Waiting for first tick…"}</span>}
            </div>
            <div className="mt-1.5 flex items-center gap-2 pointer-events-auto">
              <button data-testid="chart-sell-btn" onClick={() => { ui.setTradeIntent({ ctype: "PUT", at: Date.now() }); ui.openPanel("trade"); }} className="min-w-[84px] md:min-w-[104px] px-2 py-1 rounded-md border border-sell/70 bg-panel/80 backdrop-blur text-center hover:bg-sell/15">
                <div className="font-mono-nums text-[13px] font-semibold text-sell">{price != null ? fmtPrice(price, m.decimals) : "—"}</div><div className="text-[9px] tracking-widest text-sell/80">SELL</div>
              </button>
              <span className="text-[10px] font-mono-nums text-muted-foreground">{quote?.bid != null && quote?.ask != null ? fmtPrice(quote.ask - quote.bid, m.decimals) : "0.00"}</span>
              <button data-testid="chart-buy-btn" onClick={() => { ui.setTradeIntent({ ctype: "CALL", at: Date.now() }); ui.openPanel("trade"); }} className="min-w-[84px] md:min-w-[104px] px-2 py-1 rounded-md border border-[#3B82F6]/70 bg-panel/80 backdrop-blur text-center hover:bg-[#3B82F6]/15">
                <div className="font-mono-nums text-[13px] font-semibold text-[#3B82F6]">{price != null ? fmtPrice(price, m.decimals) : "—"}</div><div className="text-[9px] tracking-widest text-[#3B82F6]/80">BUY</div>
              </button>
              <span className="text-[10px] font-mono-nums text-muted-foreground ml-1" data-testid="chart-countdown" title="Next candle">{tf.kind === "tick" ? `${countdown}/${tf.ticks}T` : `${String(Math.floor(countdown / 60)).padStart(2, "0")}:${String(countdown % 60).padStart(2, "0")}`}</span>
            </div>
          </div>
          <ChartCanvas
            candles={displayCandles}
            chartType={chartType}
            overlays={overlays}
            panels={panels}
            showVolume={showVolume}
            structure={structure}
            structureFlags={structureFlags}
            drawings={visibleDrawings}
            activeTool={activeTool}
            toolMeta={toolMeta}
            futureFraction={chartSettings.futureFraction ?? 0.1}
            onChartReady={(c) => { chartRef.current = c; }}
            snap={snap}
            symbol={symbol}
            selectedId={dr.selectedId}
            onAddDrawing={addDrawing}
            onDrawingsChange={(list) => { const byId = new Map(list.map((d) => [d.id, d])); dr.replaceAll(dr.drawings.map((d) => byId.get(d.id) ?? d)); }}
            onSelect={dr.setSelectedId}
            onEditText={editText}
            theme={theme}
            decimals={m.decimals}
            tfSeconds={tf.kind === "tick" ? 1 : tf.seconds}
            scaleMode={ui.scaleMode}
            onHover={setHover}
            onContextMenu={setCtxMenu}
          />
          <ConfluenceCard result={confluence} cisd={structure?.cisd ?? null} t50={structure?.t50 ?? null} />
          <T50DetailCard ev={t50Detail} decimals={m.decimals} onClose={() => setT50Detail(null)} />
          <ObjectToolbar drawing={selected} tfId={tf.id} onUpdate={dr.update} onRemove={dr.remove} onDuplicate={dr.duplicate} onDeselect={() => dr.setSelectedId(null)} />
          <ChartContextMenu
            target={ctxMenu}
            decimals={m.decimals}
            onClose={() => setCtxMenu(null)}
            onReplayFrom={(c) => replayEngine.start(symbol, c.time)}
            onAlertAt={(p) => openManager({ symbol, kind: "price", operator: p > (lastPrice ?? p) ? "cross_above" : "cross_below", params: { value: +p.toFixed(m.decimals) }, timeframe: tf.id })}
            onHLineAt={(p) => dr.add(createDrawing("hline", symbol, [{ time: ctxMenu?.time ?? (candles[candles.length - 1]?.time ?? 0), price: p }]))}
            onDeleteDrawing={(id) => dr.remove(id)}
            onDuplicateDrawing={(id) => dr.duplicate(id)}
            onCopyPrice={(p) => navigator.clipboard?.writeText(p.toFixed(m.decimals))}
          />
        </div>
      </div>
      {isReplaying(symbol) && <ReplayBar tf={tf} />}
      <div className="h-8 px-2 border-t border-border flex items-center gap-0.5 text-[11px] shrink-0 overflow-x-auto scrollbar-none whitespace-nowrap [&>*]:shrink-0" data-testid="chart-bottom-bar">
        {[["1D", "5m"], ["5D", "15m"], ["1M", "1h"], ["3M", "4h"], ["6M", "4h"], ["YTD", "1D"], ["1Y", "1D"], ["5Y", "1D"], ["All", "1D"]].map(([l, t]) => (
          <button key={l} data-testid={`range-${l}`} onClick={() => { onTfChange(TIMEFRAMES.find((x) => x.id === t) || tf); setTimeout(() => chartRef.current?.resetView(), 50); }} className="px-2 h-6 rounded text-muted-foreground hover:bg-secondary hover:text-foreground font-medium">{l}</button>
        ))}
        <span className="ml-auto font-mono-nums text-muted-foreground hidden md:inline" data-testid="chart-clock" title="Deriv server time (backend-synchronised clock, RTT-corrected)">DERIV {clock} UTC</span>
        <div className="h-4 w-px bg-border mx-2 ml-auto md:ml-2" />
        <button data-testid="chart-scale-percent" onClick={() => ui.setScaleMode(ui.scaleMode === "percent" ? "normal" : "percent")} title="Percent scale (vs latest close)" className={`px-1.5 h-6 rounded font-medium hover:bg-secondary ${ui.scaleMode === "percent" ? "text-primary" : "text-muted-foreground"}`}>%</button>
        <button data-testid="chart-scale-log" onClick={() => ui.setScaleMode(ui.scaleMode === "log" ? "normal" : "log")} title="Logarithmic price scale" className={`px-1.5 h-6 rounded font-medium hover:bg-secondary ${ui.scaleMode === "log" ? "text-primary" : "text-muted-foreground"}`}>log</button>
        <button data-testid="chart-auto-btn" onClick={() => chartRef.current?.goToLive()} className="px-1.5 h-6 rounded text-primary hover:bg-secondary font-medium" title="Auto scale + go to live">auto</button>
      </div>
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, title, testid, active = false }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className={`h-8 w-8 rounded-md flex items-center justify-center hover:bg-secondary hover:text-foreground transition-colors ${active ? "text-primary" : "text-muted-foreground"}`}>
      <Icon className="h-4 w-4" />
    </button>
  );
}
